import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";

/**
 * Confirmation dialog shown when admin assigns a driver to a passenger that
 * has same-time-frame siblings (same flight or same shared-ride group).
 *
 * Renders nothing if `cascade` is null/undefined. This is by design so the
 * parent can safely render `<CascadeAssignDialog cascade={cascade} ... />`
 * even before the cascade state has been computed — no ReferenceError risk.
 */
export function CascadeAssignDialog({ cascade, selectedBooking, onCancel, onAssign }) {
  if (!cascade) return null;

  const available = cascade.available || [];
  const blocked = cascade.blocked || [];
  const rule = cascade.rule;

  return (
    <AlertDialog open onOpenChange={(o) => !o && onCancel()}>
      <AlertDialogContent data-testid="cascade-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Also assign to other passengers?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm">
              <p>
                Found <span className="font-bold">{available.length}</span> other passenger{available.length !== 1 ? "s" : ""} in the same{" "}
                {rule === "flight"
                  ? <span className="font-bold">flight ({selectedBooking?.flight_number})</span>
                  : <span className="font-bold">pickup group</span>}.
              </p>
              <ul className="bg-slate-50 rounded p-2 max-h-40 overflow-y-auto space-y-0.5 text-xs">
                {available.map((p) => (
                  <li key={p.id} data-testid={`cascade-available-${p.id}`}>
                    • <span className="font-medium">{p.full_name}</span>{" "}
                    <span className="text-slate-500">— {p.travel_time} · {p.flight_number || p.pickup_location}</span>
                  </li>
                ))}
              </ul>
              {blocked.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs">
                  <p className="font-bold text-amber-900 mb-1">
                    {blocked.length} already assigned to another driver — will be skipped:
                  </p>
                  <ul className="text-amber-800">
                    {blocked.map((p) => (
                      <li key={p.id}>• {p.full_name} → {p.current_driver_name}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2">
          <AlertDialogCancel data-testid="cascade-cancel-btn">Cancel</AlertDialogCancel>
          <Button
            variant="outline"
            onClick={() => onAssign(false)}
            data-testid="cascade-only-this-btn"
          >
            Only this passenger
          </Button>
          <AlertDialogAction
            onClick={() => onAssign(true)}
            data-testid="cascade-confirm-all-btn"
            className="bg-green-600 hover:bg-green-700"
          >
            Assign to all {1 + available.length}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
